export const getYearsOptions = (currentYear, nbYears, startingYear = 0) => {
  if (startingYear > 0) {
    currentYear += startingYear;
  }
  let nextYearCampaign = currentYear + 1;
  let yearsOptions = [];
  for (let i = 0; i < nbYears - startingYear; i++) {
    yearsOptions.push({
      id: nextYearCampaign + i,
      value: `${currentYear + i} - ${nextYearCampaign + i}`,
    });
  }
  return yearsOptions;
};

export const buildRecapData = ({
  parcelsIds,
  culturesIds,
  cultureMinMax,
  constraintsNames,
  constraintValues,
  strategies,
  parcelConstraints,
  exploitationParcels,
  culturesResult,
  t,
}) => {
  const CONSTRAINT_LABELS = {
    irrigation: t('simulation.step.constraints.labels.irrigationVolume'),
    irrigableParcelNumber: t('simulation.step.constraints.labels.irrigableParcelNumber'),
    activateCAPCategory: t('simulation.step.constraints.labels.activateCAP'),
    winterCrop: t('simulation.constraints.winterCrop'),
    cerealePaille: t('simulation.constraints.cerealCulture'),
    TFIHerbicideConstraint: t('simulation.constraints.maxIftHerbicide'),
    TFIWithoutHerbicideConstraint: t('simulation.constraints.maxIftNonHerbicide'),
    nitrogen: t('common.nitrogen'),
    coverProposal: t('simulation.constraints.coverProposal'),
    extraBiomass: t('simulation.constraints.extraBiomass'),
    intercropSurface: t('simulation.step.constraints.labels.maxSurface'),
  };

  const parcelsList = parcelsIds?.map((id) => exploitationParcels?.find((p) => p.id === id)?.name);

  const culturesList = culturesIds?.map((id) => {
    const culture = culturesResult?.find((c) => c.id === id);
    const constraints = cultureMinMax[id];
    return {
      name: culture?.name,
      min: constraints?.min || 0,
      max: constraints?.max || 0,
      unit: constraints?.unit || 'ha',
    };
  });

  const constraintsList = constraintsNames?.map((name) => {
    const values = constraintValues[name];
    let label = CONSTRAINT_LABELS[name] || name;
    if (
      values?.min !== '' &&
      values?.min !== undefined &&
      values?.max !== '' &&
      values?.max !== undefined
    ) {
      label += ` (Min: ${values.min}, Max: ${values.max})`;
    } else if (values?.min !== '' && values?.min !== undefined) {
      label += ` (Min: ${values.min})`;
    } else if (values?.max !== '' && values?.max !== undefined) {
      label += ` (Max: ${values.max})`;
    }
    return label;
  });

  const STRATEGIES_DISPLAY_NAMES = {
    MAX_GM: t('simulation.common.freeOptimization'),
    HISTORY: t('simulation.common.historyOptimization'),
  };

  const strategiesList =
    strategies?.map((s) => {
      const name = typeof s === 'string' ? s : s?.name || s;
      return STRATEGIES_DISPLAY_NAMES[name] || name;
    }) || [];
  const parcelConstraintsList = parcelConstraints?.map((constraint) => {
    const typeLabels = {
      obligation: t('simulation.step.parcelConstraints.constraintTypes.obligation'),
      interdiction: t('simulation.step.parcelConstraints.constraintTypes.interdiction'),
      division: t('simulation.step.parcelConstraints.constraintTypes.division'),
      permission: t('simulation.step.parcelConstraints.constraintTypes.permission'),
      conversion: t('simulation.common.transitionBio'),
    };
    const typeLabel = typeLabels[constraint.constraint] || constraint.constraint;
    const cultureNames = constraint.cultures
      ?.map((cId) => culturesResult?.find((c) => c.id === cId)?.name)
      .join(', ');
    const parcelNames = constraint.parcels
      ?.map((pId) => exploitationParcels?.find((p) => p.id === pId)?.name)
      .join(', ');
    const campagnesLabel = constraint.years?.join(', ');
    return { type: constraint.constraint, typeLabel, cultureNames, parcelNames, campagnesLabel };
  });

  return { parcelsList, culturesList, constraintsList, strategiesList, parcelConstraintsList };
};

// Mapping from French database strings to translation keys for PAC categories
export const PAC_CATEGORY_TRANSLATION_MAP = {
  "Céréales d'hiver": 'simulation.result.pac.categories.winterCereals',
  'Céréales de printemps': 'simulation.result.pac.categories.springCereals',
  'Plantes sarclées': 'simulation.result.pac.categories.rootCrops',
  "Oléagineux d'hiver": 'simulation.result.pac.categories.winterOilseeds',
  'Oléagineux de printemps': 'simulation.result.pac.categories.springOilseeds',
  'Prairies Temporaires Et Jachère': 'simulation.result.pac.categories.meddowFallow',
  "Fixatrices d'Azote": 'simulation.result.pac.categories.nitrogenFixing',
  'Cultures Diverses': 'simulation.result.pac.categories.diverseCultures',
  'Autre culture TA': 'simulation.result.pac.categories.otherCulture',
  'Prairies Permanentes': 'simulation.result.pac.categories.permanentGrassland',
  Total: 'common.total',
};

/**
 * Translates a PAC category from French (database value) to the current language
 * @param {string} frenchCategory - The category name in French from the database
 * @param {Function} t - The translation function from useTranslation hook
 * @returns {string} The translated category name
 */
export const translatePacCategory = (frenchCategory, t) => {
  const translationKey = PAC_CATEGORY_TRANSLATION_MAP[frenchCategory];
  return translationKey ? t(translationKey) : frenchCategory;
};

// Mapping from French database constraint displayNames to translation keys
export const CONSTRAINT_DISPLAY_NAME_MAP = {
  'Volume maximal par an (m3)': 'simulation.step.constraints.labels.irrigationVolume',
  'Nombre de parcelles irrigables': 'simulation.step.constraints.labels.irrigableParcelNumber',
};

/**
 * Translates a constraint displayName from French (database value) to the current language
 * @param {string} frenchDisplayName - The constraint displayName in French from the database
 * @param {Function} t - The translation function from useTranslation hook
 * @returns {string} The translated displayName
 */
export const translateConstraintDisplayName = (frenchDisplayName, t) => {
  const translationKey = CONSTRAINT_DISPLAY_NAME_MAP[frenchDisplayName];
  return translationKey ? t(translationKey) : frenchDisplayName;
};
